<template>
  <v-list dense>
    <v-list-item class="avatar">
      <v-list-item-icon>
        <v-icon>mdi-account-outline</v-icon>
      </v-list-item-icon>
      <v-list-item-title>
        <div class="title-icon">
          <span>
            <h6 class="title mb-0">{{ currentUser.firstName + ' ' + currentUser.lastName }}</h6>
          </span>
          <v-menu offset-y>
            <template v-slot:activator="{on}">
              <v-icon v-on="on">$sl-menu</v-icon>
            </template>
            <v-list dense>
              <v-list-item @click="$nextTick(() => $router.push({name: 'User', params: {id: currentUser.id}}))">
                <v-list-item-icon> <v-icon>$sl-user</v-icon> </v-list-item-icon>Min profil
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </v-list-item-title>
    </v-list-item>
    <v-list-item to="/" link>
      <v-list-item-icon>
        <v-icon>$sl-monitor-chart</v-icon>
      </v-list-item-icon>
      <v-list-item-title class="v-step-1">Översikt</v-list-item-title>
    </v-list-item>

    <template v-for="(item, index) in items">
      <template v-if="item.children">
        <v-list-group no-action sub-group :key="index">
          <template v-slot:activator>
            <v-list-item-content>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item-content>
          </template>

          <v-list-item v-for="(child, i) in item.children" :key="i" link :to="child.to">
            <v-icon v-text="child.icon"></v-icon>

            <v-list-item-title v-text="child.title"></v-list-item-title>
          </v-list-item>
        </v-list-group>
      </template>

      <template v-else>
        <v-list-item
          :data-cy="`menu-${item.title}`"
          exact
          :key="index"
          :to="item.to"
          v-if="
            typeof item.enforce === 'undefined' ||
            (item.enforce &&
              currentPermissions[item.enforce[0]] &&
              currentPermissions[item.enforce[0]][item.enforce[1]])
          "
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-title :class="item.class">{{ item.title }}</v-list-item-title>
        </v-list-item>
      </template>
    </template>

    <template v-if="currentPermissions.admin && currentPermissions.admin.list">
      <v-divider class></v-divider>

      <v-list-group no-action>
        <template v-slot:activator>
          <v-list-item-icon>
            <v-icon>$sl-shield-settings</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Administration</v-list-item-title>
          </v-list-item-content>
        </template>

        <v-list-item v-for="(admin, i) in adminItems" :key="i" link :to="admin.to" class="child" v-if="!admin.canDisplay || admin.canDisplay(currentUser)">
            <v-list-item-icon>
              <v-icon v-text="admin.icon"></v-icon>
            </v-list-item-icon>
            <v-list-item-title v-text="admin.title"></v-list-item-title>
        </v-list-item>
      </v-list-group>
    </template>

    <v-divider class></v-divider>

    <v-list-item @click="logout">
      <v-list-item-icon>
        <v-icon>$sl-logout</v-icon>
      </v-list-item-icon>
      <v-list-item-title>Logga ut</v-list-item-title>
    </v-list-item>

    <v-list-item class="mt-8" target="_blank" href="https://byns.sharepoint.com/SitePages/Hitta-anvisningar.aspx">
      <v-list-item-icon>
        <v-icon>$sl-book-flip-page</v-icon>
      </v-list-item-icon>
      <v-list-item-title>BYN:s anvisningar</v-list-item-title>
    </v-list-item>

    <v-list-item @click="openGuide" link>
      <v-list-item-icon>
        <v-icon>$sl-outdoors-landscape</v-icon>
      </v-list-item-icon>
      <v-list-item-title>Guide</v-list-item-title>
    </v-list-item>

    <v-list-item @click="clickHelp">
      <v-list-item-icon>
        <v-icon>$sl-question-circle</v-icon>
      </v-list-item-icon>
      <v-list-item-content>
        <v-list-item-title>Hjälp</v-list-item-title>
      </v-list-item-content>
    </v-list-item>

    <v-list-item href="mailto:support@byn.se" target="_blank">
      <v-list-item-icon>
        <v-icon>$sl-send</v-icon>
      </v-list-item-icon>
      <v-list-item-content>
        <v-list-item-title>Support</v-list-item-title>
      </v-list-item-content>
    </v-list-item>

    <v-list-item @click="$events.fire('showAbout', '')">
      <v-list-item-icon>
        <v-icon>$sl-information-circle</v-icon>
      </v-list-item-icon>
      <v-list-item-content>
        <v-list-item-title>Om Registret</v-list-item-title>
      </v-list-item-content>
    </v-list-item>
  </v-list>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
// @group Common
// MainMenu component
export default {
  name: 'MainMenu',

  data: () => ({
    items: [
      {
        title: 'Personer',
        icon: '$sl-multiple-neutral',
        to: {name: 'PersonList'},
        enforce: ['apprentice', 'list'],
        class: 'v-step-2'
      },
      {
        title: 'Företag',
        icon: '$sl-bulding-day-light',
        to: {name: 'Companies'},
        enforce: ['company', 'list'],
        class: 'v-step-3'
      },
      {
        title: 'Anmälningar',
        icon: '$sl-business-contract-approve',
        to: '/applications',
        enforce: ['application', 'list'],
        class: 'v-step-4'
      },
      {
        title: 'Beställningar',
        icon: '$sl-notes-paper-approve',
        to: {name: 'Orders'},
        enforce: ['order', 'list'],
        class: 'v-step-5'
      },
      {
        title: 'Statistik',
        icon: '$sl-statistics',
        to: '/statistics',
        enforce: ['application', 'list'],
        class: 'v-step-6'
      },
      {
        title: 'Användare',
        icon: '$sl-staff',
        to: '/users',
        enforce: ['user', 'list'],
        class: 'v-step-7'
      }
    ],
    adminItems: [
      {
        title: 'Anställningstyper',
        icon: '$sl-multiple-actions',
        to: {name: 'AdminTypes', params: {item: 'employmenttype'}}
      },
      {title: 'Avtalstyper', icon: '$sl-business-contract', to: {name: 'AdminTypes', params: {item: 'contracttype'}}},
      {
        title: 'Yrken',
        icon: '$sl-profession-man-construction',
        to: {name: 'AdminTypes', params: {item: 'professions'}}
      },
      {title: 'Yrkeskurser', icon: '$sl-school-teacher', to: {name: 'ProfessionCourses'}},
      {title: 'Breddning', icon: '$sl-widening', to: {name: 'ProfessionWidening'}},
      {title: 'Distansutbildningar', icon: '$sl-elearning-book-smartphone', to: {name: 'DistanceEducations'}},
      {title: 'E-boksmoment', icon: '$sl-toys-lego', to: {name: 'BookElements'}},
      {title: 'Inställningar', icon: '$sl-cog-square', to: {name: 'SystemSettings'}},
      {title: 'Handledarinstruktioner', icon: '$sl-book-flip-page', to: {name: 'InstructorInstructions'}},
      {title: 'Misslyckade distansbeställningar', icon: '$sl-notes-paper-approve', canDisplay: (currentUser) => currentUser.userrole.isAdmin, to: {name: 'FailedEducations'}}
    ]
  }),

  computed: {
    ...mapGetters('currentuser', ['currentPermissions']),
    ...mapGetters('currentuser', ['currentUser'])
  },

  watch: {
    currentPermissions: {
      handler: (newVal, OldVal) => {},
      deep: true,
      immediate: true
    }
  },

  methods: {
    ...mapActions('currentuser', ['setCurrentUser', 'setNotifications']),
    /**
     * @vuese
     * Used to open site tour.
     */
    openGuide() {
      if (this.$vuetify.breakpoint.mdAndUp == true) {
        this.$events.fire('openLeftMenu')
      } else {
        this.$events.fire('closeLeftMenu')
        console.log('closeLeftMenu')
      }
      if (this.$route.path != '/') {
        this.$router.push({name: 'Start', params: {guide: true}})
      } else {
        this.$router.push({name: 'LoginReturn', params: {guide: true}})
      }
    },
    clickHelp() {
      window.open('https://byns.sharepoint.com/SitePages/Register4.aspx', '_blank')
    },
    async logout() {
      const res = await this.$dialog.confirm({
        title: 'Är du säker på att du vill logga ut?',
        text: '',
        actions: [
          {text: 'Avbryt', key: false, icon: '$sl-cancel'},
          {
            text: 'Logga ut',
            key: true,
            icon: '$sl-logout',
            outlined: true
          }
        ]
      })

      if (res == true) {
        this.setCurrentUser(null)
        this.$events.fire('loggedOut')
        if (this.$route.path != '/') {
          this.$router.push('/').catch(() => {})
        }
      } else {
        return
      }

      //this.$emit('logout')
    }

    // logout() {

    // }
  }
}
</script>

<style lang="scss" scoped>
  .title.mb-0 {
    max-width: 190px;
    text-overflow: ellipsis;
    overflow: hidden;
  }
</style>
